import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
  Paper,
  InputAdornment,
  useMediaQuery,
  useTheme,
  IconButton,
  Chip,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const FilterBar = ({
  onSearch,
  onFilterChange,
  onSortChange,
  onOpenNowChange,
  currentFilter = 'all',
  currentSort = 'random',
  isOpenNow = false,
  userLocation = null,
  locationStatus = 'pending'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] = useState(currentFilter);
  const [sortValue, setSortValue] = useState(currentSort);
  const [openNow, setOpenNow] = useState(isOpenNow);
  const [selectedHour, setSelectedHour] = useState('all');

  const sortOptions = [
    { value: 'random', label: 'Random' },
    { value: 'headline', label: 'Name' },
    {
      value: 'distance',
      label: 'Distance',
      disabled: locationStatus !== 'granted'
    }
  ];

  const dayOptions = [
    { value: 'all', label: 'All Days' },
    { value: 'sunday', label: 'Sun' },
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' }
  ];

  const hourOptions = [
    { value: 'all', label: 'Any Time' },
    { value: '12', label: '12:00' },
    { value: '13', label: '13:00' },
    { value: '14', label: '14:00' },
    { value: '15', label: '15:00' },
    { value: '16', label: '16:00' },
    { value: '17', label: '17:00' },
    { value: '18', label: '18:00' },
    { value: '19', label: '19:00' },
    { value: '20', label: '20:00' },
    { value: '21', label: '21:00' },
    { value: '22', label: '22:00' },
    { value: '23', label: '23:00' },
  ];

  useEffect(() => {
    setFilterValue(currentFilter);
  }, [currentFilter]);

  useEffect(() => {
    setSortValue(currentSort);
  }, [currentSort]);

  useEffect(() => {
    if (locationStatus === 'denied' && sortValue === 'distance') {
      setSortValue('random');
      onSortChange('random');
    }
  }, [locationStatus, sortValue, onSortChange]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleOpenNowToggle = () => {
    const newValue = !openNow;
    setOpenNow(newValue);
    onOpenNowChange(newValue);
  };

  const handleHourChange = (event) => {
    const value = event.target.value;
    setSelectedHour(value);
    onFilterChange({ day: filterValue, hour: value });
  };

  const handleDayFilterChange = (event) => {
    const value = event.target.value;
    setFilterValue(value);
    onFilterChange({ day: value, hour: selectedHour });
  };

  const handleDayChipClick = (value) => {
    setFilterValue(value);
    onFilterChange({ day: value, hour: selectedHour });
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    setSortValue(value);
    onSortChange(value);
  };

  // Mobile layout with horizontal scrolling chips
  if (isMobile) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          position: 'sticky',
          top: 56,
          zIndex: 10,
          py: 1.5,
          px: 2,
          backgroundColor: 'background.warm',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Search Field */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search venues..."
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              borderRadius: 3
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Horizontal scrolling chips */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 0.5,
            mx: -2,
            px: 2,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none'
          }}
        >
          {/* Open Now toggle button */}
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Open Now"
            onClick={handleOpenNowToggle}
            color={openNow ? 'primary' : 'default'}
            variant={openNow ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 500,
              flexShrink: 0,
              borderColor: openNow ? 'primary.main' : 'divider',
              '&:hover': {
                backgroundColor: openNow ? 'primary.dark' : 'rgba(217, 119, 6, 0.08)'
              }
            }}
          />

          {/* Day filter chips */}
          {dayOptions.map((option) => (
            <Chip
              key={option.value}
              icon={option.value !== 'all' ? <CalendarTodayIcon sx={{ fontSize: 14 }} /> : undefined}
              label={option.label}
              onClick={() => handleDayChipClick(option.value)}
              color={filterValue === option.value ? 'primary' : 'default'}
              variant={filterValue === option.value ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 500,
                flexShrink: 0,
                borderColor: filterValue === option.value ? 'primary.main' : 'divider',
                '&:hover': {
                  backgroundColor: filterValue === option.value ? 'primary.dark' : 'rgba(217, 119, 6, 0.08)'
                }
              }}
            />
          ))}
        </Box>

        {/* Second row: Time and Sort selects */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={selectedHour}
              onChange={handleHourChange}
              displayEmpty
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 3,
                '& .MuiSelect-select': { py: 1 }
              }}
              startAdornment={
                <InputAdornment position="start">
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              {hourOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={sortValue}
              onChange={handleSortChange}
              displayEmpty
              sx={{
                backgroundColor: 'background.paper',
                borderRadius: 3,
                '& .MuiSelect-select': { py: 1 }
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    );
  }

  // Desktop layout - single row
  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 10,
        py: 2,
        backgroundColor: 'background.warm',
        borderRadius: 0,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          {/* Search Field */}
          <TextField
            variant="outlined"
            placeholder="Search venues..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Open Now toggle */}
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label="Open Now"
            onClick={handleOpenNowToggle}
            color={openNow ? 'primary' : 'default'}
            variant={openNow ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 500,
              px: 1,
              height: 36,
              borderColor: openNow ? 'primary.main' : 'divider',
              '&:hover': {
                backgroundColor: openNow ? 'primary.dark' : 'rgba(217, 119, 6, 0.08)'
              }
            }}
          />

          {/* Day Filter */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Day</InputLabel>
            <Select
              value={filterValue}
              label="Day"
              onChange={handleDayFilterChange}
              sx={{ backgroundColor: 'background.paper' }}
              startAdornment={
                <InputAdornment position="start">
                  <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Days</MenuItem>
              <MenuItem value="sunday">Sunday</MenuItem>
              <MenuItem value="monday">Monday</MenuItem>
              <MenuItem value="tuesday">Tuesday</MenuItem>
              <MenuItem value="wednesday">Wednesday</MenuItem>
              <MenuItem value="thursday">Thursday</MenuItem>
              <MenuItem value="friday">Friday</MenuItem>
              <MenuItem value="saturday">Saturday</MenuItem>
            </Select>
          </FormControl>

          {/* Time Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time</InputLabel>
            <Select
              value={selectedHour}
              label="Time"
              onChange={handleHourChange}
              sx={{ backgroundColor: 'background.paper' }}
              startAdornment={
                <InputAdornment position="start">
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              {hourOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              value={sortValue}
              label="Sort"
              onChange={handleSortChange}
              sx={{ backgroundColor: 'background.paper' }}
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              }
            >
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Container>
    </Box>
  );
};

export default FilterBar;
